import React, { useState, useEffect, useRef } from 'react'; // useEffect kept for global command handler
import CommandInput from './CommandInput';
import CommandOutput from './CommandOutput';
import PromptLine from './PromptLine';
import { HistoryEntry } from '../types';
import { handleCommand, isValidCommandInput } from '../commands/commandHandler';
import '../styles/Terminal.css';

const WelcomeMessage: React.FC = () => (
  <div>
    Welcome to my portfolio.
    <br />
    <br />
    Explore projects, experience, and skills using terminal commands.
    <br />
    Type <span style={{ color: '#ff8800', fontWeight: 'bold' }}>'help'</span> to begin.
    <br />
    <br />
    <span style={{ color: '#888' }}>Tip: Use <span style={{ color: '#ff8800', fontWeight: 'bold' }}>Tab</span> for command completion, <span style={{ color: '#ff8800', fontWeight: 'bold' }}>Arrow Up</span> for command history</span>
  </div>
);

const Terminal: React.FC = () => {
  const [history, setHistory] = useState<HistoryEntry[]>([{ command: '', output: { type: 'text', content: <WelcomeMessage /> } }]);
  const [commandHistory, setCommandHistory] = useState<string[]>([]);
  const [historyIndex, setHistoryIndex] = useState<number>(-1);
  const [pendingAutoScroll, setPendingAutoScroll] = useState<boolean>(false);
  const terminalRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const latestOutputAnchorRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Make executeCommand available globally
    (window as any).executeTerminalCommand = handleCommandSubmit;
    
    return () => {
      delete (window as any).executeTerminalCommand;
    };
  }, [history, commandHistory, historyIndex]);

  useEffect(() => {
    if (!pendingAutoScroll) return;

    const contentElement = contentRef.current;
    const outputAnchor = latestOutputAnchorRef.current;

    if (!contentElement || !outputAnchor) {
      setPendingAutoScroll(false);
      return;
    }

    const animationFrameId = window.requestAnimationFrame(() => {
      const contentRect = contentElement.getBoundingClientRect();
      const anchorRect = outputAnchor.getBoundingClientRect();
      const anchorOffsetTop = anchorRect.top - contentRect.top + contentElement.scrollTop;
      const viewportOffset = Math.max(16, Math.round(contentElement.clientHeight * 0.14));

      contentElement.scrollTo({
        top: Math.max(0, anchorOffsetTop - viewportOffset),
        behavior: 'smooth'
      });

      setPendingAutoScroll(false);
    });

    return () => window.cancelAnimationFrame(animationFrameId);
  }, [history, pendingAutoScroll]);




  const handleCommandSubmit = (input: string) => {
    const trimmedInput = input.trim();
    const shouldAutoScroll = isValidCommandInput(trimmedInput);
    const output = handleCommand(input);
    
    if (output.type === 'text' && output.content === 'CLEAR') {
      setHistory([{ command: '', output: { type: 'text', content: <WelcomeMessage /> } }]);
      setPendingAutoScroll(false);
      return;
    }

    setHistory([...history, { command: input, output }]);
    setCommandHistory([...commandHistory, input]);
    setHistoryIndex(-1);
    setPendingAutoScroll(shouldAutoScroll);
  };

  const getHistoryCommand = (direction: 'up' | 'down'): string => {
    if (commandHistory.length === 0) return '';

    let newIndex = historyIndex;

    if (direction === 'up') {
      newIndex = historyIndex < commandHistory.length - 1 ? historyIndex + 1 : historyIndex;
    } else {
      newIndex = historyIndex > -1 ? historyIndex - 1 : -1;
    }

    setHistoryIndex(newIndex);
    return newIndex === -1 ? '' : commandHistory[commandHistory.length - 1 - newIndex];
  };

  return (
    <div className="terminal" ref={terminalRef} onClick={() => {
      const selection = window.getSelection();
      if (selection && selection.toString().length > 0) return;
      document.getElementById('command-input')?.focus({ preventScroll: true });
    }}>
      <div className="terminal-header">
        <div className="window-controls">
          <span className="control-btn close"></span>
          <span className="control-btn minimize"></span>
          <span className="control-btn maximize"></span>
        </div>
        <div className="terminal-title">
          <svg className="file-icon" width="16" height="16" viewBox="0 0 24 24" fill="none">
            <path d="M3 6.5C3 5.11929 4.11929 4 5.5 4H9.08579C9.351 4 9.60536 4.10536 9.79289 4.29289L11.2071 5.70711C11.3946 5.89464 11.649 6 11.9142 6H18.5C19.8807 6 21 7.11929 21 8.5V17.5C21 18.8807 19.8807 20 18.5 20H5.5C4.11929 20 3 18.8807 3 17.5V6.5Z" fill="#5C9FD6"/>
          </svg>
          anupam@portfolio
        </div>
      </div>
      <div className="terminal-content" ref={contentRef}>
        {history.map((entry, index) => {
          const isLatestEntry = index === history.length - 1;

          return (
            <div key={index}>
              {entry.command && <PromptLine command={entry.command} />}
              {entry.output && (
                <>
                  {isLatestEntry && entry.command && <div className="output-anchor" ref={latestOutputAnchorRef} aria-hidden="true" />}
                  <CommandOutput output={entry.output} />
                </>
              )}
            </div>
          );
        })}
        <CommandInput onCommandSubmit={handleCommandSubmit} getHistoryCommand={getHistoryCommand} />
      </div>
    </div>
  );
};

export default Terminal;